// src/pages/EditClient.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const EditClient = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Fixed database configuration (cannot be edited)
  const FIXED_DB_CONFIG = {
    db_user: 'wims_user',
    db_password: 'warals@123', // Fixed value since API doesn't return it
    db_host: 'localhost',
    db_port: 5432,
  };

  const [formData, setFormData] = useState({
    client_name: '',
    client_code: '',
    database_name: '',
    db_user: FIXED_DB_CONFIG.db_user,
    db_password: FIXED_DB_CONFIG.db_password,
    db_host: FIXED_DB_CONFIG.db_host,
    db_port: FIXED_DB_CONFIG.db_port,
    connection_string: '',
    email: '',
    mobile_no: '',
    address: '',
    website_link: '',
    client_logo: '',
    password: '',
    employee_name: '',
    user_type: 'Admin',
    expiry_date: '',
    is_active: true,
  });

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [touched, setTouched] = useState({});
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success', // 'success', 'error', 'info', 'warning'
  });
  
  // Dialog state for confirmation
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null,
  });

  const token = localStorage.getItem('accessToken');

  // Validation function
  const validateField = (name, value) => {
    switch (name) {
      case 'client_name':
        if (!value || value.trim() === '') return 'Client name is required';
        if (value.length < 2) return 'Client name must be at least 2 characters';
        if (value.length > 100) return 'Client name must be less than 100 characters';
        if (!/^[a-zA-Z\s\-'&]+$/.test(value)) return 'Client name contains invalid characters';
        return '';
      
      case 'client_code':
        if (!value || value.trim() === '') return 'Client code is required';
        if (value.length < 2) return 'Client code must be at least 2 characters';
        if (value.length > 20) return 'Client code must be less than 20 characters';
        if (!/^[A-Z0-9_]+$/i.test(value)) return 'Client code can only contain letters, numbers, and underscores';
        return '';
      
      case 'database_name':
        if (!value || value.trim() === '') return 'Database name is required';
        if (value.length < 2) return 'Database name must be at least 2 characters';
        if (value.length > 50) return 'Database name must be less than 50 characters';
        if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(value)) return 'Database name must start with a letter and contain only letters, numbers, and underscores';
        return '';
      
      case 'email':
        if (!value || value.trim() === '') return 'Email is required';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) return 'Please enter a valid email address';
        if (value.length > 100) return 'Email must be less than 100 characters';
        return '';
      
      case 'mobile_no':
        if (value && value.trim() !== '') {
          const mobileRegex = /^[0-9+\-\s()]{10,15}$/;
          if (!mobileRegex.test(value)) return 'Please enter a valid mobile number (10-15 digits)';
        }
        return '';
      
      case 'address':
        if (value && value.length > 500) return 'Address must be less than 500 characters';
        return '';
      
      case 'website_link':
        if (value && value.trim() !== '') {
          const urlRegex = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/i;
          if (!urlRegex.test(value)) return 'Please enter a valid website URL';
        }
        return '';
      
      case 'client_logo':
        if (value && value.trim() !== '') {
          const urlRegex = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/i;
          if (!urlRegex.test(value)) return 'Please enter a valid logo URL';
        }
        return '';
      
      case 'password':
        if (value && value.trim() !== '') {
          if (value.length < 6) return 'Password must be at least 6 characters';
          if (value.length > 50) return 'Password must be less than 50 characters';
          if (!/(?=.*[A-Za-z])(?=.*\d)/.test(value)) return 'Password must contain both letters and numbers';
        }
        return '';
      
      case 'employee_name':
        if (!value || value.trim() === '') return 'Employee name is required';
        if (value.length < 2) return 'Employee name must be at least 2 characters';
        if (value.length > 100) return 'Employee name must be less than 100 characters';
        if (!/^[a-zA-Z\s\-']+$/.test(value)) return 'Employee name can only contain letters, spaces, hyphens, and apostrophes';
        return '';
      
      case 'expiry_date':
        if (!value) return 'Expiry date is required';
        const selectedDate = new Date(value);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (selectedDate < today) return 'Expiry date cannot be in the past';
        return '';
      
      default:
        return '';
    }
  };

  // Validate all fields
  const validateAllFields = () => {
    const errors = {};
    const fieldsToValidate = ['client_name', 'client_code', 'database_name', 'email', 'employee_name', 'expiry_date'];
    
    fieldsToValidate.forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) errors[field] = [error];
    });
    
    // Optional fields validation
    const optionalFields = ['mobile_no', 'address', 'website_link', 'client_logo', 'password'];
    optionalFields.forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) errors[field] = [error];
    });
    
    return errors;
  };

// useEffect(() => {
//   if (formData.database_name && formData.database_name.trim() !== '') {
//     const { db_host, db_port, db_user, db_password } = FIXED_DB_CONFIG;
//     const generated = `host=${db_host} port=${db_port} dbname=${formData.database_name} user=${db_user} password=${db_password}`;
    
//     setFormData((prev) => ({
//       ...prev,
//       connection_string: generated,
//     }));
//   }
// }, [formData.database_name]);

  // Fetch existing client
  useEffect(() => {
    if (!id || !token) {
      setSnackbar({
        open: true,
        message: 'Invalid client ID or authentication missing',
        severity: 'error',
      });
      setLoading(false);
      return;
    }

const fetchClient = async () => {
  try {
    setLoading(true);
    const response = await fetch(`${API_BASE_URL}/api/core/GetClients/${id}/`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to load client (HTTP ${response.status})`);
    }

    const data = await response.json();
    
    // Get the database_name from API response
    const databaseName = data.database_name || '';
    
    // Generate NEW connection string using FIXED values (not the API values)
    const { db_host, db_port, db_user, db_password } = FIXED_DB_CONFIG;
    const newConnectionString = databaseName 
      ? `host=${db_host} port=${db_port} dbname=${databaseName} user=${db_user} password=${db_password}`
      : '';

    // Set form data - USE FIXED VALUES for DB fields, NOT the API response
    setFormData({
      client_name: data.client_name || '',
      client_code: data.client_code || '',
      database_name: databaseName,  // Use database_name from API
      db_user: FIXED_DB_CONFIG.db_user,  // Override with fixed value
      db_password: FIXED_DB_CONFIG.db_password,  // Override with fixed value
      db_host: FIXED_DB_CONFIG.db_host,  // Override with fixed value
      db_port: FIXED_DB_CONFIG.db_port,  // Override with fixed value
      connection_string: newConnectionString,  // Use newly generated connection string
      email: data.email || '',
      mobile_no: data.mobile_no || '',
      address: data.address || '',
      website_link: data.website_link || '',
      client_logo: data.client_logo || '',
      password: '',  // Keep empty for security
      employee_name: data.employee_name || '',
      user_type: data.user_type === 'client_admin' ? 'Admin' : (data.user_type || 'Admin'),
      expiry_date: data.expiry_date ? data.expiry_date.split('T')[0] : '',
      is_active: data.is_active ?? true,
    });
  } catch (err) {
    console.error(err);
    setSnackbar({
      open: true,
      message: err.message || 'Could not load client data',
      severity: 'error',
    });
  } finally {
    setLoading(false);
  }
};

    fetchClient();
  }, [id, token]);

// Update the restricted fields in handleChange:
const handleChange = (e) => {
  const { name, value, type, checked } = e.target;
  
  // Prevent editing of restricted fields (database_name is NOT here because it's disabled in UI)
  const restrictedFields = ['db_user', 'db_password', 'db_host', 'db_port', 'connection_string'];
  if (restrictedFields.includes(name)) {
    return;
  }

  setFormData((prev) => ({
    ...prev,
    [name]: type === 'checkbox' ? checked : value,
  }));

  // Clear field error on change
  if (formErrors[name]) {
    setFormErrors((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }
};

  const handleBlur = (fieldName) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }));
    
    const error = validateField(fieldName, formData[fieldName]);
    if (error) {
      setFormErrors((prev) => ({ ...prev, [fieldName]: [error] }));
    } else {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleConfirmDialogClose = () => {
    setConfirmDialog({ ...confirmDialog, open: false });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Mark all fields as touched
    const allFields = ['client_name', 'client_code', 'database_name', 'email', 'employee_name', 'expiry_date'];
    const touchedFields = {};
    allFields.forEach(field => { touchedFields[field] = true; });
    setTouched(touchedFields);
    
    // Validate all fields before submission
    const errors = validateAllFields();
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setSnackbar({
        open: true,
        message: 'Please fix the validation errors before submitting.',
        severity: 'error',
      });
      return;
    }
    
    // Show confirmation dialog
    setConfirmDialog({
      open: true,
      title: 'Confirm Update',
      message: 'Are you sure you want to update this client?',
      onConfirm: async () => {
        setConfirmDialog({ ...confirmDialog, open: false });
        await submitForm();
      },
    });
  };

  const submitForm = async () => {
    setSubmitting(true);
    setFormErrors({});

    if (!token) {
      setSnackbar({
        open: true,
        message: 'Authentication token missing. Please log in again.',
        severity: 'error',
      });
      setSubmitting(false);
      return;
    }

    // Ensure connection string is present
    if (!formData.connection_string?.trim()) {
      setFormErrors((prev) => ({
        ...prev,
        connection_string: ['Connection string is required'],
      }));
      setSnackbar({
        open: true,
        message: 'Connection string is required',
        severity: 'error',
      });
      setSubmitting(false);
      return;
    }

    try {
      const payload = { ...formData };
      
      // Remove empty password (don't send if not changed)
      if (!payload.password?.trim()) {
        delete payload.password;
      }
      
      // Ensure fixed DB values are sent
      payload.db_user = FIXED_DB_CONFIG.db_user;
      payload.db_password = FIXED_DB_CONFIG.db_password;
      payload.db_host = FIXED_DB_CONFIG.db_host;
      payload.db_port = FIXED_DB_CONFIG.db_port;

      const response = await fetch(`${API_BASE_URL}/api/core/InsertUpdateClient/${id}/`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));

        if (typeof errData === 'object' && !Array.isArray(errData)) {
          // Map server validation errors to form errors
          const serverErrors = {};
          Object.keys(errData).forEach(key => {
            if (Array.isArray(errData[key])) {
              serverErrors[key] = errData[key];
            } else if (typeof errData[key] === 'string') {
              serverErrors[key] = [errData[key]];
            } else {
              serverErrors[key] = [String(errData[key])];
            }
          });
          setFormErrors(serverErrors);
          throw new Error('Validation errors occurred');
        }

        throw new Error(
          errData.detail ||
          errData.message ||
          errData.non_field_errors?.[0] ||
          `Server error (${response.status})`
        );
      }

      setSnackbar({
        open: true,
        message: 'Client updated successfully!',
        severity: 'success',
      });
      
      setTimeout(() => navigate('/warals/manage-client'), 1600);
    } catch (err) {
      console.error('Update error:', err);
      
      setSnackbar({
        open: true,
        message: err.message || 'Failed to update client. Please try again.',
        severity: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h4" fontWeight={600} gutterBottom>
        Edit Client #{id}
      </Typography>

      <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2.5}>
            {/* Client Name */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Client Name"
                name="client_name"
                value={formData.client_name}
                onChange={handleChange}
                onBlur={() => handleBlur('client_name')}
                error={!!formErrors.client_name && touched.client_name}
                helperText={(touched.client_name && formErrors.client_name?.[0]) || ''}
              />
            </Grid>

            {/* Client Code */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Client Code"
                name="client_code"
                value={formData.client_code}
                onChange={handleChange}
                onBlur={() => handleBlur('client_code')}
                error={!!formErrors.client_code && touched.client_code}
                helperText={(touched.client_code && formErrors.client_code?.[0]) || 'Only letters, numbers, and underscores'}
              />
            </Grid>

            {/* Database Name - DISABLED (cannot edit) */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Database Name"
                name="database_name"
                value={formData.database_name}
                disabled
                helperText="Database name cannot be edited after creation"
                sx={{
                  '& .MuiInputBase-root.Mui-disabled': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
              />
            </Grid>

            {/* DB User - DISABLED & Fixed */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="DB User"
                name="db_user"
                value={FIXED_DB_CONFIG.db_user}
                disabled
                helperText="Fixed value - cannot be edited"
                sx={{
                  '& .MuiInputBase-root.Mui-disabled': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
              />
            </Grid>

            {/* DB Password - DISABLED & Fixed (hidden) */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="password"
                label="DB Password"
                name="db_password"
                value="********"
                disabled
                helperText="Auto-configured - value hidden for security"
                sx={{
                  '& .MuiInputBase-root.Mui-disabled': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
              />
            </Grid>

            {/* DB Host - DISABLED & Fixed */}
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="DB Host"
                name="db_host"
                value={FIXED_DB_CONFIG.db_host}
                disabled
                helperText="Fixed value - cannot be edited"
                sx={{
                  '& .MuiInputBase-root.Mui-disabled': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
              />
            </Grid>

            {/* DB Port - DISABLED & Fixed */}
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="DB Port"
                name="db_port"
                value={FIXED_DB_CONFIG.db_port}
                disabled
                helperText="Fixed value - cannot be edited"
                sx={{
                  '& .MuiInputBase-root.Mui-disabled': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
              />
            </Grid>

            {/* Connection String - READ ONLY */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Connection String"
                name="connection_string"
                value={formData.connection_string}
                InputProps={{
                  readOnly: true,
                }}
                helperText="Auto-generated - read only"
                sx={{
                  '& .MuiInputBase-root.Mui-readOnly': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
              />
            </Grid>

            {/* Email */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="email"
                label="Email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={() => handleBlur('email')}
                error={!!formErrors.email && touched.email}
                helperText={(touched.email && formErrors.email?.[0]) || ''}
              />
            </Grid>

            {/* Mobile Number */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Mobile Number"
                name="mobile_no"
                value={formData.mobile_no}
                onChange={handleChange}
                onBlur={() => handleBlur('mobile_no')}
                error={!!formErrors.mobile_no && touched.mobile_no}
                helperText={(touched.mobile_no && formErrors.mobile_no?.[0]) || 'Optional - 10-15 digits'}
              />
            </Grid>

            {/* Address */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                onBlur={() => handleBlur('address')}
                error={!!formErrors.address && touched.address}
                helperText={(touched.address && formErrors.address?.[0]) || 'Optional'}
              />
            </Grid>

            {/* Website Link */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Website Link"
                name="website_link"
                value={formData.website_link}
                onChange={handleChange}
                onBlur={() => handleBlur('website_link')}
                error={!!formErrors.website_link && touched.website_link}
                helperText={(touched.website_link && formErrors.website_link?.[0]) || 'Optional - e.g., https://example.com'}
              />
            </Grid>

            {/* Client Logo URL */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Client Logo URL"
                name="client_logo"
                value={formData.client_logo}
                onChange={handleChange}
                onBlur={() => handleBlur('client_logo')}
                error={!!formErrors.client_logo && touched.client_logo}
                helperText={(touched.client_logo && formErrors.client_logo?.[0]) || 'Optional - Image URL'}
              />
            </Grid>

            {/* Admin Password - Optional */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="password"
                label="Admin Password (optional)"
                name="password"
                value={formData.password}
                onChange={handleChange}
                onBlur={() => handleBlur('password')}
                error={!!formErrors.password && touched.password}
                helperText={(touched.password && formErrors.password?.[0]) || 'Only fill if you want to change the password (min 6 chars with letters & numbers)'}
              />
            </Grid>

            {/* Employee Name */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Employee Name"
                name="employee_name"
                value={formData.employee_name}
                onChange={handleChange}
                onBlur={() => handleBlur('employee_name')}
                error={!!formErrors.employee_name && touched.employee_name}
                helperText={(touched.employee_name && formErrors.employee_name?.[0]) || ''}
              />
            </Grid>

            {/* User Type */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="User Type"
                name="user_type"
                value={formData.user_type}
                onChange={handleChange}
                error={!!formErrors.user_type}
                helperText={formErrors.user_type?.[0] || ''}
                SelectProps={{ native: true }}
              >
                <option value="Admin">Admin</option>
                <option value="Manager">Manager</option>
                <option value="User">User</option>
              </TextField>
            </Grid>

            {/* Expiry Date */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="date"
                label="Expiry Date"
                name="expiry_date"
                value={formData.expiry_date}
                onChange={handleChange}
                onBlur={() => handleBlur('expiry_date')}
                error={!!formErrors.expiry_date && touched.expiry_date}
                helperText={(touched.expiry_date && formErrors.expiry_date?.[0]) || 'Cannot be in the past'}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Is Active */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleChange}
                    color="primary"
                  />
                }
                label="Is Active"
              />
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12} sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/warals/manage-client')}
                disabled={submitting}
               data-app-cancel="true">
                Cancel
              </Button>

              <Button
                type="submit"
                variant="contained"
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {submitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={handleConfirmDialogClose}
      >
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmDialogClose} color="primary" data-app-cancel="true">
            Cancel
          </Button>
          <Button onClick={confirmDialog.onConfirm} color="primary" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EditClient;